import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BullhornFieldType, Entities } from '../../data-rules-model';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { IEntityItem, PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IFieldMeta, MetaDataService } from '../../meta-data.service';

@Component({
  selector: 'app-compare-criteria',
  templateUrl: './compare-criteria.component.html',
  imports: [
    PairedAutocompleteComponent
  ]
})
export class CompareCriteriaComponent implements OnInit, OnChanges, OnDestroy {

  @Input() primaryEntity: Entities;
  @Input() entitiesOptions: IEntityItem[];
  @Input() selectedFieldType: string;
  @Input() selectedFieldToOneType: string | null;
  @Input() sourceFieldName: string;
  @Input() sourceSecondaryDescriptor: string;
  @Input() overlayPositioningConfig: ICustomPositioningConfig;
  @Input() initialValue: string | null;

  @Output() fieldSelected = new EventEmitter<string | null>();

  @ViewChild('compareFieldPicker')
  private set compareFieldPickerComponent(component: PairedAutocompleteComponent) {
    this._compareFieldPickerComponent = component;
    if (component) {
      this.loadCompareFieldOptions();
    }
  }
  private get compareFieldPickerComponent(): PairedAutocompleteComponent {
    return this._compareFieldPickerComponent;
  }
  private _compareFieldPickerComponent: PairedAutocompleteComponent;

  compareFieldControl = new FormControl(null);
  compareFieldSecondaryDescriptorControl = new FormControl(null);

  private subscriptions = new Subscription();
  private loadOptions$ = new Subject<string | null>();

  constructor(private metaDataService: MetaDataService) {}

  ngOnInit(): void {
    if (this.initialValue) {
      if (this.initialValue.includes('.')) {
        const dotIndex = this.initialValue.indexOf('.');
        this.compareFieldSecondaryDescriptorControl.setValue(this.initialValue.substring(0, dotIndex));
        this.compareFieldControl.setValue(this.initialValue.substring(dotIndex + 1));
      } else {
        this.compareFieldControl.setValue(this.initialValue);
      }
    }

    this.subscriptions.add(
      this.loadOptions$.pipe(
        switchMap(descriptor => this.metaDataService.filterMetaBySecondaryDescriptor(this.primaryEntity, descriptor))
      ).subscribe(filteredMeta => this.applyFilteredOptions(filteredMeta))
    );
    this.subscriptions.add(
      this.compareFieldControl.valueChanges.subscribe(val => this.onCompareFieldSelected(val))
    );
    this.subscriptions.add(
      this.compareFieldSecondaryDescriptorControl.valueChanges.subscribe(() => this.loadCompareFieldOptions())
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fieldTypeChanged = changes['selectedFieldType'];
    const sourceFieldChanged = changes['sourceFieldName'];
    if ((fieldTypeChanged && !fieldTypeChanged.firstChange) || (sourceFieldChanged && !sourceFieldChanged.firstChange)) {
      this.clearSelection();
      this.loadCompareFieldOptions();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  clearSelection(): void {
    this.compareFieldControl.setValue(null);
    this.compareFieldSecondaryDescriptorControl.setValue(null);
    if (this.compareFieldPickerComponent) {
      this.compareFieldPickerComponent.clearOption();
    }
  }

  private loadCompareFieldOptions(): void {
    this.loadOptions$.next(this.compareFieldSecondaryDescriptorControl.value);
  }

  private applyFilteredOptions(filteredMeta: IFieldMeta[]): void {
    if (!this.compareFieldPickerComponent) {
      return;
    }
    const matching = filteredMeta.filter(fm => {
      const isDirectMatch = fm.metaType === this.selectedFieldType;
      const toOneThenInt = this.selectedFieldType === BullhornFieldType.TO_ONE && fm.metaType === BullhornFieldType.INTEGER;
      const intThenToOne = this.selectedFieldType === BullhornFieldType.INTEGER && fm.metaType === BullhornFieldType.TO_ONE;
      const isIntToOneCross = toOneThenInt || intThenToOne;
      const isMatchingToOneRelation = this.selectedFieldType !== BullhornFieldType.TO_ONE
        || fm.metaType !== BullhornFieldType.TO_ONE
        || fm.toOneType === this.selectedFieldToOneType;
      const isSourceField = fm.fieldName === this.sourceFieldName && this.compareFieldSecondaryDescriptorControl.value === this.sourceSecondaryDescriptor;
      return (isDirectMatch || isIntToOneCross) && isMatchingToOneRelation && !isSourceField;
    });
    const provider = new StaticOptionsProvider(
      matching.map((fm: IFieldMeta) => ({name: fm.fieldName, label: fm.label}))
    );
    setTimeout(() => {
      if (this.compareFieldPickerComponent) {
        this.compareFieldPickerComponent.setOptionsProvider(provider, this.compareFieldControl.value);
      }
    }, 0);
  }

  private onCompareFieldSelected(fieldName: string): void {
    const descriptor = this.compareFieldSecondaryDescriptorControl.value;
    const fullFieldName = descriptor && fieldName ? `${descriptor}.${fieldName}` : fieldName;
    this.fieldSelected.emit(fullFieldName || null);
  }
}
